import uniq from 'lodash/uniq';
import isNil from 'lodash/isNil';
import {
  InspectionInterface,
  IdRef,
  TemplatesInterface,
  Job,
} from '../../types';
import { getFormattedString, stringListBuilder } from '../string';

export const inspectionStringListBuilder = (arr: (string | null)[]): string => {
  const validStrings = uniq(arr.filter((str) => str && str !== '')) as string[];
  return validStrings.map((str) => {
    let output = str;
    if (!['re_inspection', 're_visit', 'pre_inspection'].includes(str) && !str.includes('practice_')) {
      output = str.replace(/_inspection$/g, '');
    }
    return getFormattedString(output);
  }).join(', ');
};

const getInspectionTypeValues = (
  inspections: InspectionInterface,
  inspectionIds: IdRef[],
  isMock = false,
): string[] => (
  inspectionIds.reduce((acc, id) => {
    if (inspections?.[id]?.inspectionType) {
      const subtype = inspections[id].inspectionSubtype ? ` (${inspections[id].inspectionSubtype})` : '';
      acc.push(isMock ? `practice_${inspections[id].inspectionType}${subtype}` : `${inspections[id].inspectionType}${subtype}`);
    }
    return acc;
  }, [] as string[])
);

export const getInspectionTypes = (
  inspections: InspectionInterface,
  inspectionIds: IdRef[],
): string => {
  const inspectionTypes = getInspectionTypeValues(inspections, inspectionIds);
  return inspectionTypes.length > 0 ? stringListBuilder(inspectionTypes) : '-';
};

export const getFormattedInspectionTypes = (
  inspections: InspectionInterface,
  inspectionIds: IdRef[],
  isMock = false,
): string => {
  const inspectionTypes = getInspectionTypeValues(inspections, inspectionIds, isMock);
  return inspectionTypes.length > 0 ? inspectionStringListBuilder(inspectionTypes) : '-';
};

export const getInspectionTemplateIds = (
  inspections: InspectionInterface,
  inspectionIds: IdRef[],
): string[] => uniq(inspectionIds.reduce((acc, id) => {
  if (!isNil(inspections?.[id]?.templateReportId)) {
    acc.push(inspections[id].templateReportId as string);
  }
  return acc;
}, [] as string[]));

export const getInspectionTemplates = (
  inspections: InspectionInterface,
  inspectionIds: IdRef[],
  templates: TemplatesInterface,
): string => {
  const templateReportIds = getInspectionTemplateIds(inspections, inspectionIds);

  const templateNames = templateReportIds
    .filter((templateId) => templates?.[templateId]?.report?.name)
    .map((templateId) => templates[templateId].report.name);

  return templateNames.length > 0 ? templateNames.join(', ') : '-';
};

export const isGeotaggingRequired = (
  inspections: InspectionInterface,
  inspectionIds: IdRef[],
) => inspectionIds.some((id) => inspections?.[id]?.geotaggingRequired);

type ComplianceCheckOptions = {
  templateIds: string[],
  templates: TemplatesInterface;
};

export const isDenverRentalComplianceInspection = ({
  templateIds,
  templates,
}: ComplianceCheckOptions) => {
  const denverInspections = templateIds.filter((templateId) => (
    (templates?.[templateId]?.report?.name || '').toLocaleLowerCase().includes('denver rental compliance')
  ));
  return denverInspections.length > 0;
};

export const isReInspection = (id: IdRef, inspections: InspectionInterface) => {
  if (inspections[id]?.inspectionType) {
    const type = inspections[id]?.inspectionType;
    const match = type.match(/^re.?inspection$/gi) || [];
    return match.length > 0;
  }
  return false;
};

export const getReInspection = (job: Job, inspections: InspectionInterface) => {
  const reInspectionId = job.inspectionIds.find((id) => isReInspection(id, inspections));
  return reInspectionId ? inspections[reInspectionId] : null;
};
